// CB:BEGIN ui-status-badge
import React from 'react';

// Canonical status → display config map
const STATUS_MAP: Record<string, { label: string; bg: string; color: string }> = {
  // Project statuses
  DRAFT:                       { label: 'Draft',                bg: '#fef9c3', color: '#854d0e' },
  REQUESTED:                   { label: 'Requested',            bg: '#dbeafe', color: '#1d4ed8' },
  POSTED:                      { label: 'Posted',               bg: '#dbeafe', color: '#1d4ed8' },
  BIDS_COLLECTING:             { label: 'Collecting Bids',      bg: '#fef3c7', color: '#92400e' },
  BIDS_READY:                  { label: 'Bids Ready',           bg: '#dcfce7', color: '#166534' },
  DECIDING:                    { label: 'Deciding',             bg: '#fef3c7', color: '#92400e' },
  BID_SELECTED:                { label: 'Bid Selected',         bg: '#e0f2fe', color: '#075985' },
  HOMEOWNER_CONTRACT_SIGNING:  { label: 'Contract Signing',     bg: '#f3e8ff', color: '#6d28d9' },
  ESCROW_PENDING:              { label: 'Escrow Pending',       bg: '#fef3c7', color: '#92400e' },
  SCHEDULED:                   { label: 'Scheduled',            bg: '#e0f2fe', color: '#075985' },
  IN_PROGRESS:                 { label: 'In Progress',          bg: '#e0f2fe', color: '#0369a1' },
  ACTIVE_P1:                   { label: 'Phase 1',              bg: '#e0f2fe', color: '#0369a1' },
  ACTIVE_P2:                   { label: 'Phase 2',              bg: '#bfdbfe', color: '#1d4ed8' },
  ACTIVE_P3:                   { label: 'Phase 3',              bg: '#a5f3fc', color: '#0e7490' },
  COMPLETED:                   { label: 'Completed',            bg: '#dcfce7', color: '#166534' },
  CANCELLED:                   { label: 'Cancelled',            bg: '#f1f5f9', color: '#64748b' },
  CLOSED:                      { label: 'Closed',               bg: '#f1f5f9', color: '#64748b' },
  DISPUTED:                    { label: 'Disputed',             bg: '#fee2e2', color: '#dc2626' },
  // Dispute statuses
  OPEN:                        { label: 'Open',                 bg: '#fee2e2', color: '#dc2626' },
  ESCALATED:                   { label: 'Escalated',            bg: '#fff7ed', color: '#ea580c' },
  INVESTIGATING:               { label: 'Investigating',        bg: '#fefce8', color: '#ca8a04' },
  RESOLVED:                    { label: 'Resolved',             bg: '#dcfce7', color: '#166534' },
  // Bid/contractor statuses
  PENDING:                     { label: 'Pending',              bg: '#fef3c7', color: '#92400e' },
  ACCEPTED:                    { label: 'Accepted',             bg: '#dcfce7', color: '#166534' },
  REJECTED:                    { label: 'Rejected',             bg: '#fee2e2', color: '#dc2626' },
  VERIFIED:                    { label: 'Verified',             bg: '#dcfce7', color: '#166534' },
  UNVERIFIED:                  { label: 'Unverified',           bg: '#fef3c7', color: '#92400e' },
  SUSPENDED:                   { label: 'Suspended',            bg: '#fee2e2', color: '#dc2626' },
  ACTIVE:                      { label: 'Active',               bg: '#dcfce7', color: '#166534' },
  INACTIVE:                    { label: 'Inactive',             bg: '#f1f5f9', color: '#64748b' },
  // Payment / payout statuses
  FUNDED:                      { label: 'Funded',               bg: '#dcfce7', color: '#166534' },
  RELEASED:                    { label: 'Released',             bg: '#dcfce7', color: '#166534' },
  PAID:                        { label: 'Paid',                 bg: '#dcfce7', color: '#166534' },
  UNPAID:                      { label: 'Unpaid',               bg: '#fef3c7', color: '#92400e' },
  FAILED:                      { label: 'Failed',               bg: '#fee2e2', color: '#dc2626' },
  // Realtor referral statuses
  COLLECTING_BIDS:             { label: 'Collecting Bids',      bg: '#fef3c7', color: '#92400e' },
  READY:                       { label: 'Ready',                bg: '#dcfce7', color: '#166534' },
  INVITED:                     { label: 'Invited',              bg: '#e0f2fe', color: '#0369a1' },
  CONVERTED:                   { label: 'Converted',            bg: '#dcfce7', color: '#166534' },
  EXPIRED:                     { label: 'Expired',              bg: '#f1f5f9', color: '#64748b' },
  // Portal invite lifecycle statuses (Phase 5–8)
  SENT:                        { label: 'Sent',                 bg: '#ede9fe', color: '#5b21b6' },
  OPENED:                      { label: 'Opened',               bg: '#fef9c3', color: '#854d0e' },
  CLAIMED:                     { label: 'Claimed',              bg: '#dcfce7', color: '#166534' },
  REVOKED:                     { label: 'Revoked',              bg: '#fee2e2', color: '#991b1b' },
  // Incentive statuses
  PENDING_CONVERSION:          { label: 'Pending',              bg: '#f1f5f9', color: '#64748b' },
  ELIGIBLE:                    { label: 'Eligible',             bg: '#fef3c7', color: '#92400e' },
  APPROVED:                    { label: 'Approved',             bg: '#f3e8ff', color: '#6d28d9' },
  CLAWED_BACK:                 { label: 'Clawed Back',          bg: '#fee2e2', color: '#dc2626' },
  VOID:                        { label: 'Void',                 bg: '#f1f5f9', color: '#94a3b8' },
  // Agreement / verification
  NEEDS_ACTION:                { label: 'Needs Action',         bg: '#fff7ed', color: '#ea580c' },
  SIGNED:                      { label: 'Signed',               bg: '#dcfce7', color: '#166534' },
  UPLOADED:                    { label: 'Uploaded',             bg: '#dbeafe', color: '#1d4ed8' },
  MISSING:                     { label: 'Missing',              bg: '#fee2e2', color: '#dc2626' },
};

interface StatusBadgeProps {
  status: string;
  /** Override the display label */
  label?: string;
  size?: 'sm' | 'md';
  className?: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, label, size = 'sm', className }) => {
  const config = STATUS_MAP[status] ?? { label: status, bg: '#f1f5f9', color: '#374151' };
  const displayLabel = label ?? config.label;

  const padding = size === 'md' ? '4px 12px' : '3px 8px';
  const fontSize = size === 'md' ? 13 : 11;

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding,
        borderRadius: 99,
        background: config.bg,
        color: config.color,
        fontSize,
        fontWeight: 600,
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        letterSpacing: '0.01em',
      }}
    >
      {displayLabel}
    </span>
  );
};

export default StatusBadge;
// CB:END ui-status-badge
